import { DialogComponent } from '@theme/dialog';
import { CartAddEvent, ThemeEvents } from '@theme/events';

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    window.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    
    // Listen for cart update events to update total
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    
    // Check if we should open cart drawer after page reload
    if (sessionStorage.getItem('openCartDrawer') === 'true') {
      sessionStorage.removeItem('openCartDrawer');
      setTimeout(() => {
        this.showDialog();
      }, 100);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    window.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
  }

  #handleCartAdd = async (event) => {
    console.log('Cart drawer handling cart add:', event.detail);
    
    // Update cart drawer with fresh data first
    try {
      await this.#updateCartFromAPI();
    } catch (error) {
      console.error('Failed to update cart drawer content:', error);
      // If update fails but drawer is already open, that's still better than not opening at all
    }
    
    // Open drawer after content is updated if auto-open is enabled
    if (this.hasAttribute('auto-open')) {
      // Small delay to ensure DOM is fully updated
      setTimeout(() => {
        this.showDialog();
      }, 100);
    }
  };

  #updateCartContent = (sections) => {
    return new Promise((resolve) => {
      const cartItemsComponent = this.querySelector('cart-items-component');
      if (!cartItemsComponent || !sections) {
        resolve();
        return;
      }

      console.log('Cart items component found:', cartItemsComponent);
      console.log('Section ID:', cartItemsComponent.dataset.sectionId);

      // Find the section that matches this cart drawer
      const sectionId = cartItemsComponent.dataset.sectionId;
      if (sections[sectionId]) {
        console.log('Found matching section, updating content');
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = sections[sectionId];
        const newCartItems = tempDiv.querySelector('cart-items-component');
        
        if (newCartItems) {
          cartItemsComponent.innerHTML = newCartItems.innerHTML;
          console.log('Cart drawer content updated');
        }
      } else {
        console.log('No matching section found for ID:', sectionId);
      }
      
      // Small delay to ensure DOM is updated
      setTimeout(resolve, 50);
    });
  };

  #updateCartFromAPI = async () => {
    try {
      console.log('Updating cart from API...');
      
      // Fetch current cart data
      const cartResponse = await fetch('/cart.js');
      const cartData = await cartResponse.json();
      
      console.log('Cart data:', cartData);
      
      // Update cart drawer content based on cart data
      const cartItemsComponent = this.querySelector('cart-items-component');
      if (!cartItemsComponent) return;
      
      if (cartData.items && cartData.items.length > 0) {
        // Cart has items - rebuild the cart drawer content
        await this.#rebuildCartDrawer(cartData);
      } else {
        // Cart is empty - show empty state
        this.#showEmptyCart();
      }
      
    } catch (error) {
      console.error('Failed to update cart from API:', error);
      // Fallback: reload page
      window.location.reload();
    }
  };

  #rebuildCartDrawer = async (cartData) => {
    // Fetch the cart page to get the updated HTML structure
    try {
      const response = await fetch('/cart');
      const html = await response.text();
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = html;
      
      const newCartItems = tempDiv.querySelector('cart-items-component');
      const currentCartItems = this.querySelector('cart-items-component');
      
      if (newCartItems && currentCartItems) {
        // Update the cart items wrapper
        const cartItemsWrapper = newCartItems.querySelector('.cart-items__wrapper');
        const currentWrapper = currentCartItems.querySelector('.cart-items__wrapper');
        
        if (cartItemsWrapper && currentWrapper) {
          currentWrapper.innerHTML = cartItemsWrapper.innerHTML;
        } else {
          // Fallback: replace entire component but maintain drawer structure
          currentCartItems.innerHTML = newCartItems.innerHTML;
        }
        
        // Also update the cart summary section
        const newCartSummary = tempDiv.querySelector('.cart-drawer__summary');
        const currentCartSummary = this.querySelector('.cart-drawer__summary');
        
        if (newCartSummary && currentCartSummary) {
          currentCartSummary.innerHTML = newCartSummary.innerHTML;
          console.log('Cart summary updated');
        } else {
          // If cart summary doesn't exist, create it
          if (newCartSummary && !currentCartSummary) {
            const cartDrawerInner = this.querySelector('.cart-drawer__inner');
            if (cartDrawerInner) {
              cartDrawerInner.appendChild(newCartSummary.cloneNode(true));
              console.log('Cart summary added');
            }
          }
        }
        
        // Update the drawer to show non-empty state
        this.#updateDrawerState(false);
        
        // Hide empty cart message if it exists
        this.#hideEmptyCartMessage();
        
        console.log('Cart drawer rebuilt with fresh data');
      }
    } catch (error) {
      console.error('Failed to rebuild cart drawer:', error);
    }
  };

  #showEmptyCart = () => {
    // Update the drawer to show empty state
    this.#updateDrawerState(true);
    console.log('Cart drawer set to empty state');
  };

  #updateDrawerState = (isEmpty) => {
    const dialog = this.querySelector('.cart-drawer__dialog');
    if (!dialog) return;
    
    if (isEmpty) {
      dialog.classList.add('cart-drawer--empty');
    } else {
      dialog.classList.remove('cart-drawer--empty');
    }
  };

  #hideEmptyCartMessage = () => {
    const emptyMessage = this.querySelector('.cart-drawer__heading--empty');
    if (emptyMessage) {
      emptyMessage.style.display = 'none';
      console.log('Empty cart message hidden');
    }
  };

  /**
   * Handles cart update events to update the total
   */
  #handleCartUpdate = (event) => {
    // Update cart total when cart is updated
    setTimeout(() => {
      this.updateCartTotal();
    }, 100); // Small delay to ensure cart data is updated
  };

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  /**
   * Updates the cart total from the server
   */
  updateCartTotal() {
    fetch('/cart.js')
      .then(response => response.json())
      .then(cart => {
        const totalValue = document.querySelector('.cart__total-value');
        if (totalValue) {
          // Format the price in Turkish Lira
          const formattedPrice = (cart.total_price / 100).toLocaleString('tr-TR', { 
            style: 'currency', 
            currency: 'TRY' 
          });
          totalValue.textContent = formattedPrice;
          
          // Also update the value attribute if it exists
          if (totalValue.hasAttribute('value')) {
            totalValue.setAttribute('value', formattedPrice);
          }
        }
      })
      .catch(error => {
        console.error('Failed to update cart total:', error);
      });
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
